import { z } from 'zod';
import { formatSort, parseSort as parseSortKeys } from '@/components/data-table/tableSort';
import { isAction, type Effective, type SortKey, type SortState } from './compareModel';

const SORT_KEYS: readonly SortKey[] = ['record', 'date', 'source', 'action'];

/** `record.asc` ↔ { key: 'record', dir: 'asc' }; chuỗi lạ → không sắp xếp. */
const parseSort = (v: unknown): SortState | null => parseSortKeys(v, SORT_KEYS);
/** Danh sách giá trị trong URL: `AD-002,AD-003`; mỗi giá trị được mã hoá để dấu phẩy trong khoá không tách nhầm. */
export const parseList = (v: unknown): string[] =>
  typeof v === 'string' ? v.split(',').map((x) => { try { return decodeURIComponent(x); } catch { return x; } }).filter((x) => x !== '') : [];
export const formatList = (l: readonly string[]): string | undefined => (l.length > 0 ? l.map((x) => encodeURIComponent(x)).join(',') : undefined);
/** Hành động hợp lệ, không trùng, theo thứ tự cố định; `all` (đường cũ) và giá trị lạ bị bỏ. */
const parseActions = (v: unknown): Effective[] => {
  const given = new Set(parseList(v));
  return (['write', 'fix', 'conflict', 'gone', 'skipped'] as const).filter((k) => given.has(k) && isAction(k));
};
const formatActions = (a: readonly Effective[]): string | undefined => (a.length > 0 ? a.join(',') : undefined);

// Mặc định (`all`, không sắp xếp) vắng khỏi URL (rule 10); giá trị lạ coi như không có.
// `mode=compare` là đường cũ (trước khi Nhân sự / Chấm công có tab Chờ duyệt riêng): chỉ còn để chuyển hướng bookmark cũ, không ghi lại nữa.
export const syncSearchSchema = z.object({
  mode: z.unknown().transform((v) => (v === 'compare' ? ('compare' as const) : undefined)).optional(),
  /** Hành động đang lọc: danh sách (`fix,conflict`); giá trị đơn kiểu cũ (`fix`) vẫn đọc được, `all` và giá trị lạ = không lọc. */
  f: z.unknown().transform((v) => formatActions(parseActions(v))).optional(),
  s: z.unknown().transform((v) => formatSort(parseSort(v))).optional(),
  /** Lọc theo bản ghi (dự án / người) và theo trường; mặc định (không lọc) vắng khỏi URL. */
  p: z.unknown().transform((v) => formatList(parseList(v))).optional(),
  t: z.unknown().transform((v) => formatList(parseList(v))).optional(),
});

export type SyncSearch = z.infer<typeof syncSearchSchema>;
type View = { actions: Effective[]; sort: SortState | null; projects?: string[]; fields?: string[] };

export const readSyncSearch = (s: SyncSearch): View => ({ actions: parseActions(s.f), sort: parseSort(s.s), projects: parseList(s.p), fields: parseList(s.t) });

/** Đổi chip giữ thứ tự sắp xếp và ngược lại. */
export const writeSyncSearch = (cur: View, next: Partial<View>): SyncSearch => {
  const m = { ...cur, ...next };
  return { f: formatActions(m.actions), s: formatSort(m.sort), p: formatList(m.projects ?? []), t: formatList(m.fields ?? []) };
};

/** Bộ props "trạng thái xem" của SheetReviewPage, nối với URL: mỗi route chỉ cần truyền `go`. */
export function syncViewProps(cur: View, go: (s: SyncSearch) => void) {
  return {
    actions: cur.actions, onActions: (actions: Effective[]) => go(writeSyncSearch(cur, { actions })),
    sort: cur.sort, onSort: (sort: SortState | null) => go(writeSyncSearch(cur, { sort })),
    projects: cur.projects, onProjects: (projects: string[]) => go(writeSyncSearch(cur, { projects })),
    fields: cur.fields, onFields: (fields: string[]) => go(writeSyncSearch(cur, { fields })),
  };
}

/** Bookmark cũ `…/sheet?mode=compare&f=…&s=…` → trang Chờ duyệt, giữ chip và thứ tự. Trả null khi không phải đường cũ. */
export function legacyCompareRedirect(s: SyncSearch, reviewHref: string): { to: string; search: SyncSearch } | null {
  return s.mode === 'compare' ? { to: reviewHref, search: { f: s.f, s: s.s, p: s.p, t: s.t } } : null;
}
